/**
 * A linkage template as one organization has it, in the record panel
 * (Feature 74, 3.2): its switch, its warnings, the automation it overlaps on
 * here (D11), and its last 20 runs, newest first, each with the task that
 * started it and each step's status and gate outcomes (D8). Older runs stay
 * on each first task's page. A schedule template also says when it runs
 * here (`ScheduleHere`, Feature 77 D11), and its runs name their slot.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  listOrganizationLinkageRuns,
  listOrganizationLinkages,
  setOrganizationLinkage,
} from '@optum/api-client/endpoints/organization-linkages';
import type { LinkageRun } from '@optum/api-client/types';

import type { RecordRef } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordColumn,
  RecordIdentity,
  RecordListError,
  RecordMissing,
  RecordOpen,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { slotText, viewerTimeZone } from '../../lib/cron.js';
import { relativeTime, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { LoadingBlock } from '../../ui/AsyncState.js';
import { Badge, IconTile, Inline, Stack, Typography } from '../../ui/finixt.js';
import { Toggle } from '../../ui/Toggle.js';
import { useToast } from '../../ui/useToast.js';
import { outcomeText } from '../linkages/gates.js';
import { isCall, isSchedule, startWords } from '../linkages/start.js';
import { emptyGateText, refusalText, unboundText } from '../linkages/TenantLinkages.js';
import { fromOrgLinkage, overlapsHere } from './orgAutomations.js';
import { OverlapNotice } from './OverlapNotice.js';
import { ScheduleHere } from './ScheduleHere.js';
import { useOrgAutomations } from './useOrgAutomations.js';

const STATUS_TONE = {
  completed: 'ok',
  running: 'info',
  waiting: 'neutral',
  skipped: 'neutral',
  failed: 'danger',
  cancelled: 'neutral',
} as const;

/** One run: when, its state, its first task, and each step with its gates. */
function RunRow({ run, orgId }: { run: LinkageRun; orgId: string }) {
  // A schedule run has no first task: its independent root's task heads it (Feature 77 D17).
  const head = run.first_task_id ?? run.steps.find((s) => s.task_id)?.task_id ?? null;
  return (
    <li
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        padding: '9px 0',
        borderTop: '1px solid var(--border-divider)',
      }}
    >
      <Inline gap={7} align="center" wrap={false}>
        <span className="mono" style={{ color: 'var(--text-soft)', fontSize: 'var(--text-meta)' }}>
          {/* A schedule run names the slot it fired for (Feature 77 D17). */}
          {run.scheduled_for
            ? slotText(new Date(run.scheduled_for), viewerTimeZone())
            : relativeTime(run.started_at)}
        </span>
        {head && (
          <Link className="mono" to={`/orgs/${orgId}/tasks/${head}`}>
            {shortId(head)}
          </Link>
        )}
        <Badge tone={run.status === 'open' ? 'info' : 'neutral'}>{run.status}</Badge>
      </Inline>
      {run.steps.map((step) => (
        <Typography key={step.name} variant="meta">
          <span className="mono">{step.name}</span>{' '}
          <Badge tone={STATUS_TONE[step.status]}>{step.status}</Badge>
          {step.reason ? <span className="mono"> {step.reason}</span> : null}
          {Object.entries(step.gates ?? {}).map(([from, outcome]) => (
            <span key={from}>
              {' '}
              · gate from <span className="mono">{from}</span> {outcomeText(outcome)}
            </span>
          ))}
        </Typography>
      ))}
    </li>
  );
}

export function OrgLinkageRecord({ record }: { record: RecordRef }) {
  const orgId = record.orgId ?? '';
  const key = record.id;
  const qc = useQueryClient();
  const toast = useToast();
  const listKey = qk.organizationLinkages(orgId);
  const linkages = useQuery({ queryKey: listKey, queryFn: () => listOrganizationLinkages(orgId) });
  const runs = useQuery({
    queryKey: qk.organizationLinkageRuns(orgId, key),
    queryFn: () => listOrganizationLinkageRuns(orgId, key),
  });
  const found = useOrgAutomations(orgId);
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => setOrganizationLinkage(orgId, key, enabled),
    onError: (e) => toast.error('Switch refused', refusalText(e)),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: listKey });
      void qc.invalidateQueries({ queryKey: qk.linkageTemplates() });
    },
  });

  if (linkages.isPending) return <LoadingBlock />;
  if (linkages.isError) {
    return (
      <RecordListError
        message={linkages.error instanceof Error ? linkages.error.message : 'Failed to load'}
      />
    );
  }
  const row = linkages.data.find((l) => l.key === key);
  if (!row) return <RecordMissing record={record} />;
  const empty = row.empty_gates ?? [];

  return (
    <RecordColumn>
      <RecordIdentity
        avatar={
          <IconTile icon="account_tree" size={40} tint="var(--blue-tint)" color="var(--blue)" />
        }
        eyebrow={
          <Typography variant="eyebrow">
            {row.enabled ? 'Linkage, on here' : 'Linkage, off here'}
          </Typography>
        }
        name={row.name}
        subtitle={<span className="mono">{row.key}</span>}
      />
      <Inline gap={9} align="center">
        <Toggle
          checked={row.enabled}
          aria-label={`${row.name} enabled`}
          disabled={toggle.isPending}
          onChange={(enabled) => toggle.mutate(enabled)}
        />
        <Typography variant="meta">
          {isSchedule(row.start) ? (
            <>
              {row.enabled ? 'Runs' : 'Would run'} on a schedule:{' '}
              {
                // The cron in force here: the organization's own, else the template's.
                startWords(
                  { kind: 'schedule', cron: row.effective_cron ?? row.start?.cron ?? null },
                  row.first_capability_id,
                  viewerTimeZone(),
                ).words
              }
              .
            </>
          ) : (
            <>
              {row.enabled ? 'Runs when' : 'Would run when'}{' '}
              <span className="mono">{row.first_capability_id}</span>{' '}
              {isCall(row.start) ? 'is called, beside each call' : 'completes'}.
            </>
          )}
        </Typography>
      </Inline>
      {isSchedule(row.start) && (
        <ScheduleHere key={`${row.key}:${row.cron_expression ?? ''}`} orgId={orgId} row={row} />
      )}
      <OverlapNotice
        self={fromOrgLinkage(row)}
        others={overlapsHere(found, row.key)}
        templates={false}
      />
      {(row.needs_webhook || empty.length > 0 || row.unbound_steps.length > 0) && (
        <Inline gap={7}>
          {row.needs_webhook && <Badge tone="info">needs a webhook endpoint</Badge>}
          {empty.length > 0 && <Badge tone="warn">{emptyGateText(empty)}</Badge>}
          {row.unbound_steps.length > 0 && (
            <Badge tone="warn">{unboundText(row.unbound_steps)}</Badge>
          )}
        </Inline>
      )}
      <RecordSection label="Recent runs">
        {runs.isPending ? (
          <LoadingBlock />
        ) : runs.isError ? (
          <Typography variant="meta">The runs could not be read.</Typography>
        ) : runs.data.length === 0 ? (
          <Typography variant="meta">No runs here yet.</Typography>
        ) : (
          <Stack gap={5}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {runs.data.map((run) => (
                <RunRow key={`${run.first_task_id}-${run.started_at}`} run={run} orgId={orgId} />
              ))}
            </ul>
            <Typography variant="meta">
              The last {runs.data.length === 20 ? 20 : runs.data.length}, newest first; older runs
              stay on each first task&apos;s page.
            </Typography>
          </Stack>
        )}
      </RecordSection>
      <RecordActions>
        <RecordOpen record={record} label="Open canvas" />
      </RecordActions>
    </RecordColumn>
  );
}
